// Capture errors to Sentry without statically importing the SDK.
//
// `@sentry/react` is dynamically imported on first capture so the entry chunk
// stays slim. On the first error the SDK is fetched in the background; once
// loaded it stays resident for the rest of the page lifetime.

export interface ErrorContext {
  /** Route or feature where the error happened, e.g. "MediaStudio.enhance" */
  scope?: string;
  /** Authenticated user id, if known */
  uid?: string;
  /** Photo preset or AI mode in play, if applicable */
  preset?: string;
  /** Arbitrary extra context — shown under "extra" in Sentry */
  extra?: Record<string, unknown>;
  /** Breadcrumb message to log before capture */
  breadcrumb?: string;
}

type SentryModule = typeof import("@sentry/react");

let _sentryModule: SentryModule | null = null;
let _sentryLoader: Promise<SentryModule> | null = null;

function loadSentry(): Promise<SentryModule> {
  if (_sentryModule) return Promise.resolve(_sentryModule);
  if (_sentryLoader) return _sentryLoader;
  _sentryLoader = import("@sentry/react")
    .then((mod) => {
      _sentryModule = mod;
      return mod;
    })
    .catch((err) => {
      _sentryLoader = null;
      throw err;
    });
  return _sentryLoader;
}

/**
 * Consistent client-side error capture. Adds breadcrumbs + tags + user context
 * so the Sentry issue is grouped sanely and is debuggable. Safe to call even
 * when Sentry isn't initialized — falls back to console.error.
 */
export function captureError(err: unknown, context: ErrorContext = {}): void {
  const { scope, uid, preset, extra, breadcrumb } = context;
  const dispatch = (Sentry: SentryModule) => {
    try {
      if (breadcrumb) {
        Sentry.addBreadcrumb({
          category: scope || "app",
          message: breadcrumb,
          level: "error",
        });
      }
      Sentry.withScope((s) => {
        if (scope) s.setTag("scope", scope);
        if (preset) s.setTag("preset", preset);
        if (uid) s.setUser({ id: uid });
        if (extra) {
          for (const [k, v] of Object.entries(extra)) {
            s.setExtra(k, v);
          }
        }
        Sentry.captureException(err);
      });
    } catch {
      // Sentry must never break the app
    }
  };

  if (_sentryModule) {
    // Fast path: SDK already in memory — fire synchronously so existing tests
    // that check captureException was called immediately still pass.
    dispatch(_sentryModule);
  } else {
    void loadSentry().then(dispatch).catch(() => { /* SDK failed to load */ });
  }

  if (import.meta.env.DEV) {
    console.error("[captureError]", scope || "(no scope)", err, context);
  }
}

/** Test-only — reset module cache between specs. */
export function __resetCaptureErrorForTests(): void {
  _sentryModule = null;
  _sentryLoader = null;
}

/** Test-only — synchronously preload the Sentry SDK reference so subsequent
 *  captureError() calls dispatch immediately. */
export async function __preloadSentryForTests(): Promise<void> {
  await loadSentry();
}
